import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  SearchIcon,
  FilterIcon,
  XIcon,
  SlidersIcon,
  CheckIcon,
  TagIcon,
  MapPinIcon,
  CalendarIcon,
  TruckIcon,
} from "lucide-react";

interface SearchFiltersProps {
  onSearch?: (filters: any) => void;
  onReset?: () => void;
  className?: string;
}

export default function SearchFilters({
  onSearch,
  onReset,
  className = "",
}: SearchFiltersProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState<string[]>(
    []
  );
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [availability, setAvailability] = useState<string[]>([]);

  const conditions = ["New", "Used", "Refurbished"];
  const manufacturers = [
    "Caterpillar",
    "John Deere",
    "Komatsu",
    "Volvo",
    "Haas",
    "Hitachi",
    "Liebherr",
    "Kubota",
  ];

  const years = [
    "2023",
    "2022",
    "2021",
    "2020",
    "2019",
    "2018",
    "2017",
    "2016",
    "2015",
    "Older",
  ];

  const availabilityOptions = [
    { id: "in-stock", label: "In Stock" },
    { id: "delivery", label: "Delivery Available" },
    { id: "financing", label: "Financing Available" },
  ];

  const handleSearch = () => {
    if (onSearch) {
      onSearch({
        searchTerm,
        priceRange,
        selectedConditions,
        selectedManufacturers,
        selectedYear,
        location,
        availability,
      });
    }
  };

  const handleReset = () => {
    setSearchTerm("");
    setPriceRange([0, 500000]);
    setSelectedConditions([]);
    setSelectedManufacturers([]);
    setSelectedYear("");
    setLocation("");
    setAvailability([]);

    if (onReset) {
      onReset();
    }
  };

  const handleConditionToggle = (condition: string) => {
    setSelectedConditions((prev) =>
      prev.includes(condition)
        ? prev.filter((c) => c !== condition)
        : [...prev, condition]
    );
  };

  const handleManufacturerToggle = (manufacturer: string) => {
    setSelectedManufacturers((prev) =>
      prev.includes(manufacturer)
        ? prev.filter((m) => m !== manufacturer)
        : [...prev, manufacturer]
    );
  };

  const handleAvailabilityToggle = (option: string) => {
    setAvailability((prev) =>
      prev.includes(option)
        ? prev.filter((a) => a !== option)
        : [...prev, option]
    );
  };

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-grow">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search machinery..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button onClick={handleSearch}>
            <SearchIcon className="h-4 w-4 mr-2" />
            Search
          </Button>
        </div>

        <div className="flex items-center justify-between">
          <h3 className="font-medium flex items-center">
            <FilterIcon className="h-4 w-4 mr-2" />
            Filters
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="h-8"
          >
            <XIcon className="h-3 w-3 mr-1" />
            Reset
          </Button>
        </div>
      </div>

      <Accordion
        type="multiple"
        defaultValue={["price", "condition"]}
        className="w-full"
      >
        <AccordionItem value="price">
          <AccordionTrigger className="text-sm font-medium">
            <div className="flex items-center">
              <TagIcon className="h-4 w-4 mr-2" />
              Price Range
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <Slider
                defaultValue={[0, 500000]}
                max={500000}
                step={5000}
                value={priceRange}
                onValueChange={setPriceRange}
                className="py-4"
              />

              <div className="flex items-center justify-between">
                <span className="text-sm">{formatPrice(priceRange[0])}</span>
                <span className="text-sm">{formatPrice(priceRange[1])}</span>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="condition">
          <AccordionTrigger className="text-sm font-medium">
            <div className="flex items-center">
              <SlidersIcon className="h-4 w-4 mr-2" />
              Condition
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 pt-2">
              {conditions.map((condition, index) => (
                <div key={condition} className="flex items-center space-x-2">
                  <Checkbox
                    id={`condition-${condition}`}
                    checked={selectedConditions.includes(condition)}
                    onCheckedChange={() => handleConditionToggle(condition)}
                  />

                  <Label
                    htmlFor={`condition-${condition}`}
                    className="text-sm cursor-pointer"
                  >
                    {condition}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="manufacturer">
          <AccordionTrigger className="text-sm font-medium">
            <div className="flex items-center">
              <CheckIcon className="h-4 w-4 mr-2" />
              Manufacturer
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 pt-2 max-h-48 overflow-y-auto">
              {manufacturers.map((manufacturer, index) => (
                <div key={manufacturer} className="flex items-center space-x-2">
                  <Checkbox
                    id={`manufacturer-${manufacturer}`}
                    checked={selectedManufacturers.includes(manufacturer)}
                    onCheckedChange={() =>
                      handleManufacturerToggle(manufacturer)
                    }
                  />

                  <Label
                    htmlFor={`manufacturer-${manufacturer}`}
                    className="text-sm cursor-pointer"
                  >
                    {manufacturer}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="year">
          <AccordionTrigger className="text-sm font-medium">
            <div className="flex items-center">
              <CalendarIcon className="h-4 w-4 mr-2" />
              Year
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger>
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year, index) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="location">
          <AccordionTrigger className="text-sm font-medium">
            <div className="flex items-center">
              <MapPinIcon className="h-4 w-4 mr-2" />
              Location
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <Input
              placeholder="Enter city, state, or zip"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="availability">
          <AccordionTrigger className="text-sm font-medium">
            <div className="flex items-center">
              <TruckIcon className="h-4 w-4 mr-2" />
              Availability
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 pt-2">
              {availabilityOptions.map((option, index) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`availability-${option.id}`}
                    checked={availability.includes(option.id)}
                    onCheckedChange={() => handleAvailabilityToggle(option.id)}
                  />

                  <Label
                    htmlFor={`availability-${option.id}`}
                    className="text-sm cursor-pointer"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Button className="w-full" onClick={handleSearch}>
        Apply Filters
      </Button>
    </div>
  );
}
